/**
 * Vehicle Detail Page Component
 * =============================
 * Comprehensive view of a single fleet vehicle with tabs for
 * maintenance, service history, costs, and documents.
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  Edit,
  Trash2,
  MoreVertical,
  Wrench,
  History,
  DollarSign,
  FileText,
  User,
  Building2,
  Calendar,
  Gauge,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fleetApi } from '../../services/fleetApi';

// Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-success-bg', text: 'text-success', label: 'Active' },
    maintenance: { bg: 'bg-warning-bg', text: 'text-warning', label: 'In Maintenance' },
    out_of_service: { bg: 'bg-danger-bg', text: 'text-danger', label: 'Out of Service' },
    disposed: { bg: 'bg-surface-muted', text: 'text-ink-soft', label: 'Disposed' },
  };
  const { bg, text, label } = config[status] || config.active;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Info Card
const InfoCard = ({ icon: Icon, label, value, subvalue }) => (
  <div className="bg-surface-card rounded-lg border border-border-default p-4">
    <div className="flex items-center text-ink-muted mb-2">
      <Icon className="w-4 h-4 mr-2" />
      <span className="text-sm">{label}</span>
    </div>
    <p className="text-lg font-semibold text-ink-soft">{value || '-'}</p>
    {subvalue && <p className="text-sm text-ink-muted mt-1">{subvalue}</p>}
  </div>
);

// Maintenance Schedule Tab
const MaintenanceTab = ({ fleetId, vehicleId, vehicle }) => {
  // This would fetch from maintenance_schedules table
  const schedules = [
    { id: 1, service: 'Oil Change', interval_km: 8000, last_done_km: 45000, current_km: vehicle?.current_odometer || 50000 },
    { id: 2, service: 'Tire Rotation', interval_km: 12000, last_done_km: 42000, current_km: vehicle?.current_odometer || 50000 },
    { id: 3, service: 'Brake Inspection', interval_km: 20000, last_done_km: 40000, current_km: vehicle?.current_odometer || 50000 },
    { id: 4, service: 'Air Filter', interval_km: 25000, last_done_km: 35000, current_km: vehicle?.current_odometer || 50000 },
  ];

  const getStatus = (schedule) => {
    const kmSinceLast = schedule.current_km - schedule.last_done_km;
    const kmRemaining = schedule.interval_km - kmSinceLast;
    
    if (kmRemaining < 0) return { status: 'overdue', label: `${Math.abs(kmRemaining).toLocaleString()} km overdue`, color: 'text-danger', bg: 'bg-danger-bg' };
    if (kmRemaining < 1000) return { status: 'due_soon', label: `${kmRemaining.toLocaleString()} km remaining`, color: 'text-warning', bg: 'bg-warning-bg' };
    return { status: 'ok', label: `${kmRemaining.toLocaleString()} km remaining`, color: 'text-success', bg: 'bg-success-bg' };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-ink-soft">Maintenance Schedule</h3>
        <button className="text-sm text-accent hover:text-accent-hover font-medium">
          + Add Service Item
        </button>
      </div>
      
      {schedules.map((schedule) => {
        const statusInfo = getStatus(schedule);
        return (
          <div key={schedule.id} className={`${statusInfo.bg} rounded-lg p-4 border border-border-default`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {statusInfo.status === 'overdue' && <AlertTriangle className="w-5 h-5 text-danger mr-3" />}
                {statusInfo.status === 'due_soon' && <Clock className="w-5 h-5 text-warning mr-3" />}
                {statusInfo.status === 'ok' && <CheckCircle className="w-5 h-5 text-success mr-3" />}
                <div>
                  <p className="font-medium text-ink-soft">{schedule.service}</p>
                  <p className="text-sm text-ink-muted">Every {schedule.interval_km.toLocaleString()} km</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-medium ${statusInfo.color}`}>{statusInfo.label}</p>
                <p className="text-sm text-ink-muted">Last: {schedule.last_done_km.toLocaleString()} km</p>
              </div>
            </div>
            <div className="mt-3 flex space-x-2">
              <Link
                to={`/fleet/${fleetId}/vehicles/${vehicleId}/service/new?type=${schedule.service}`}
                className="text-sm text-accent hover:text-accent-hover font-medium"
              >
                Log Service
              </Link>
              <span className="text-ink-muted">|</span>
              <button className="text-sm text-accent hover:text-accent-hover font-medium flex items-center">
                <Sparkles className="w-3 h-3 mr-1" />
                Get Cost Estimate
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Service History Tab
const ServiceHistoryTab = ({ fleetId, vehicleId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['vehicleHistory', fleetId, vehicleId],
    queryFn: () => fleetApi.getServiceHistory(fleetId, vehicleId, { page_size: 20 }),
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  if (isLoading) {
    return <div className="animate-pulse h-48 bg-surface-muted rounded-lg" />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-ink-soft">Service History</h3>
        <div className="text-sm text-ink-muted">
          Total: {formatCurrency(data?.total_cost)}
        </div>
      </div>

      {data?.records?.length > 0 ? (
        <div className="space-y-3">
          {data.records.map((record) => (
            <div key={record.id} className="bg-surface-card border border-border-default rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center">
                    <span className={`px-2 py-0.5 rounded text-small font-medium ${
                      record.service_type === 'preventive' ? 'bg-success-bg text-success' :
                      record.service_type === 'repair' ? 'bg-warning-tint text-warning' :
                      record.service_type === 'inspection' ? 'bg-accent/10 text-accent' :
                      'bg-surface-muted text-ink-soft'
                    }`}>
                      {record.service_type}
                    </span>
                    <span className="ml-2 text-sm text-ink-muted">
                      {new Date(record.service_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="mt-2 font-medium text-ink-soft">{record.description}</p>
                  {record.vendor_name && (
                    <p className="text-sm text-ink-muted mt-1">
                      Vendor: {record.vendor_name}
                    </p>
                  )}
                  {record.odometer_at_service && (
                    <p className="text-sm text-ink-muted">
                      Odometer: {record.odometer_at_service.toLocaleString()} km
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-ink-soft">
                    {formatCurrency(record.cost_total)}
                  </p>
                  {record.invoice_number && (
                    <p className="text-small text-ink-muted">#{record.invoice_number}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-surface-page rounded-lg">
          <History className="w-12 h-12 text-ink-muted mx-auto mb-3" />
          <p className="text-ink-muted">No service records yet</p>
          <Link
            to={`/fleet/${fleetId}/vehicles/${vehicleId}/service/new`}
            className="mt-4 inline-flex items-center text-accent hover:text-accent-hover font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Log First Service
          </Link>
        </div>
      )}
    </div>
  );
};

// Cost Analysis Tab
const CostsTab = ({ fleetId, vehicleId }) => {
  const { data } = useQuery({
    queryKey: ['vehicleCosts', fleetId, vehicleId],
    queryFn: () => fleetApi.getCostReport(fleetId, {
      report_type: 'by_month',
      vehicle_id: vehicleId,
      date_from: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
    }),
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-ink-soft">Cost Analysis</h3>
        <select className="text-sm border border-border-strong rounded-lg px-3 py-1">
          <option>Last 12 months</option>
          <option>This year</option>
          <option>Last year</option>
          <option>All time</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-accent/10 rounded-lg p-4">
          <p className="text-sm text-accent font-medium">Total Cost</p>
          <p className="text-2xl font-bold text-ink-soft">{formatCurrency(data?.grand_total)}</p>
        </div>
        <div className="bg-success-tint rounded-lg p-4">
          <p className="text-sm text-success font-medium">Parts</p>
          <p className="text-2xl font-bold text-ink-soft">
            {formatCurrency(data?.items?.reduce((sum, i) => sum + parseFloat(i.cost_parts || 0), 0))}
          </p>
        </div>
        <div className="bg-info-tint rounded-lg p-4">
          <p className="text-sm text-info font-medium">Labor</p>
          <p className="text-2xl font-bold text-info">
            {formatCurrency(data?.items?.reduce((sum, i) => sum + parseFloat(i.cost_labor || 0), 0))}
          </p>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-surface-card border border-border-default rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-border-default">
          <thead className="bg-surface-page">
            <tr>
              <th className="px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">Month</th>
              <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase">Services</th>
              <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase">Parts</th>
              <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase">Labor</th>
              <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {data?.items?.map((item, idx) => (
              <tr key={idx} className="hover:bg-surface-muted">
                <td className="px-4 py-3 text-sm text-ink-soft">{item.name}</td>
                <td className="px-4 py-3 text-sm text-ink-muted text-right">{item.service_count}</td>
                <td className="px-4 py-3 text-sm text-ink-muted text-right">{formatCurrency(item.cost_parts)}</td>
                <td className="px-4 py-3 text-sm text-ink-muted text-right">{formatCurrency(item.cost_labor)}</td>
                <td className="px-4 py-3 text-sm font-medium text-ink-soft text-right">{formatCurrency(item.cost_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main Component
const VehicleDetailPage = () => {
  const { fleetId, vehicleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('maintenance');
  const [showMenu, setShowMenu] = useState(false);

  const { data: vehicle, isLoading, error } = useQuery({
    queryKey: ['fleetVehicle', fleetId, vehicleId],
    queryFn: () => fleetApi.getVehicle(fleetId, vehicleId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => fleetApi.removeVehicle(fleetId, vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['fleetVehicles', fleetId]);
      navigate(`/fleet/${fleetId}/vehicles`);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-danger mx-auto mb-4" />
          <p className="text-ink-soft font-medium">Error loading vehicle</p>
          <p className="text-ink-muted">{error.message}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'history', label: 'Service History', icon: History },
    { id: 'costs', label: 'Costs', icon: DollarSign },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-default">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(`/fleet/${fleetId}/vehicles`)}
            className="flex items-center text-ink-muted hover:text-ink-soft mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Vehicles
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-surface-muted rounded-xl flex items-center justify-center">
                <Truck className="w-8 h-8 text-ink-muted" />
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-ink-soft">
                    {vehicle?.unit_number || vehicle?.vehicle_profile?.display_name}
                  </h1>
                  <StatusBadge status={vehicle?.status} />
                </div>
                <p className="text-ink-muted">
                  {vehicle?.vehicle_profile?.year} {vehicle?.vehicle_profile?.make} {vehicle?.vehicle_profile?.model}
                </p>
                <p className="text-sm text-ink-muted font-mono">
                  VIN: {vehicle?.vehicle_profile?.vin}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Link
                to={`/fleet/${fleetId}/vehicles/${vehicleId}/service/new`}
                className="inline-flex items-center px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-hover"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Log Service
              </Link>
              
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 border border-border-default rounded-lg hover:bg-surface-muted"
                >
                  <MoreVertical className="w-5 h-5 text-ink-muted" />
                </button>
                
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-surface-card rounded-lg shadow-popover border border-border-default py-1 z-20">
                      <Link
                        to={`/fleet/${fleetId}/vehicles/${vehicleId}/edit`}
                        className="flex items-center px-4 py-2 text-sm text-ink-soft hover:bg-surface-muted"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Vehicle
                      </Link>
                      <button
                        onClick={() => {
                          if (window.confirm('Remove this vehicle from the fleet?')) {
                            deleteMutation.mutate();
                          }
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-danger hover:bg-danger-bg"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Vehicle
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <InfoCard
            icon={Gauge}
            label="Odometer"
            value={vehicle?.current_odometer ? `${vehicle.current_odometer.toLocaleString()} ${vehicle.odometer_unit}` : '-'}
            subvalue={vehicle?.last_odometer_update ? `Updated ${new Date(vehicle.last_odometer_update).toLocaleDateString()}` : null}
          />
          <InfoCard
            icon={Building2}
            label="Department"
            value={vehicle?.department?.name || 'Unassigned'}
          />
          <InfoCard
            icon={User}
            label="Driver"
            value={vehicle?.assigned_driver?.display_name || 'Unassigned'}
          />
          <InfoCard
            icon={Calendar}
            label="License Plate"
            value={vehicle?.license_plate || '-'}
            subvalue={vehicle?.registration_expiry ? `Expires ${new Date(vehicle.registration_expiry).toLocaleDateString()}` : null}
          />
          <InfoCard
            icon={Calendar}
            label="Acquired"
            value={vehicle?.acquisition_date ? new Date(vehicle.acquisition_date).toLocaleDateString() : '-'}
          />
          <InfoCard
            icon={DollarSign}
            label="YTD Cost"
            value={vehicle?.total_cost_ytd ? `$${vehicle.total_cost_ytd.toLocaleString()}` : '$0'}
          />
        </div>

        {/* Tabs */}
        <div className="bg-surface-card rounded-xl border border-border-default">
          <div className="border-b border-border-default">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-accent text-accent'
                      : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-border-default'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'maintenance' && (
              <MaintenanceTab fleetId={fleetId} vehicleId={vehicleId} vehicle={vehicle} />
            )}
            {activeTab === 'history' && (
              <ServiceHistoryTab fleetId={fleetId} vehicleId={vehicleId} />
            )}
            {activeTab === 'costs' && (
              <CostsTab fleetId={fleetId} vehicleId={vehicleId} />
            )}
            {activeTab === 'documents' && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-ink-muted mx-auto mb-3" />
                <p className="text-ink-muted">Document management coming soon</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailPage;
